import { useState, type CSSProperties } from 'react';

import { Modal, Notice } from '@/components/Modal';
import { LoadingBlock } from '@/components/PageHeader';
import { errorMessage, inputStyle } from '@/lib/format';

import { EducationPicker } from './EducationPicker';
import { FORM_DESK_STYLES, previewJournal } from './formPreview';
import { DEFAULT_FORMS } from './journalForm';
import { JournalPaper } from './JournalPaper';
import {
  useEducationCatalog,
  useEducationTemplate,
  useEducationTemplateWrite,
  type EducationKind,
  type EducationSub,
} from './safeJournalApi';

/**
 * 안전교육일지 양식 편집 (V540 · DOC-122 §20) — 교육 종류마다 한 벌. 결재 칸 이름 · 6번 칸 이름 · 교육시간 · 교육대상 ·
 * 강사 · 교육자료 · 교육 내용(근거 조항 줄 + 항목)이 새 일지의 기본값이 된다. 왼쪽에서 고치면 오른쪽 종이가 바로 바뀐다
 * (사용자 지시 2026-10-23 「안전일지교육 탭도 양식별로 편집 … 새 일지 쓰는건 협력업체들이나 다른직원이 쓰고 양식편집은 또 별도로」).
 *
 * <p>고치는 사람은 안전보건팀 · 최고관리자다. 버튼은 감추지 않는다 — 그 밖의 사람이 [저장] 을 누르면 서버가 답한다.
 * 이미 쓴 일지는 그대로다 — 기본값은 새 일지를 열 때 한 번 들어갈 뿐이다.
 */
export function EducationTemplateEditor({ onClose }: { onClose: () => void }) {
  const catalog = useEducationCatalog();
  const [pick, setPick] = useState<{ kind: EducationKind; sub: string | null }>({ kind: 'WORKER', sub: 'REGULAR' });
  const template = useEducationTemplate(pick.kind, pick.sub);
  const write = useEducationTemplateWrite();
  const [draft, setDraft] = useState<Draft | null>(null);
  /** 편집 칸에 채운 양식 — 다른 교육을 고르거나 저장해 새 값이 오면 다시 채운다. */
  const [loaded, setLoaded] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const data = template.data;
  const dataKey = data ? `${data.kind}:${data.sub}:${template.dataUpdatedAt}` : null;
  if (data && dataKey !== loaded) {
    setLoaded(dataKey);
    setDraft(toDraft(data.current));
    setDirty(false);
  }

  const choose = (kind: EducationKind, sub: string | null) => {
    if (dirty && !window.confirm('저장하지 않은 양식이 있습니다. 다른 교육으로 바꿀까요?')) return;
    setError(null);
    setSaved(null);
    setDirty(false);
    setPick({ kind, sub });
  };
  const patch = (next: Partial<Draft>) => {
    setDraft((prev) => (prev ? { ...prev, ...next } : prev));
    setSaved(null);
    setDirty(true);
  };
  const orNull = (value: string) => (value.trim() === '' ? null : value.trim());
  const hours = !draft || draft.hours.trim() === '' || Number.isNaN(Number(draft.hours)) ? null : Number(draft.hours);
  // 고른 교육이 아직 이 화면의 것과 같을 때만 쓴다(다른 교육을 받는 중이면 옛 칸이다).
  const ready = !!data && !!draft && data.kind === pick.kind && data.sub === pick.sub;

  const run = (reset: boolean) => {
    setError(null);
    if (!ready || !data || !draft) {
      setError('교육 종류를 고르세요.');
      return;
    }
    if (reset && !window.confirm('이 교육의 양식을 기본값으로 되돌릴까요?')) return;
    write.mutate(
      {
        kind: data.kind,
        sub: data.sub,
        body: reset
          ? null
          : {
              signers: draft.signers.map((one) => one.trim()),
              boxLabel: orNull(draft.boxLabel),
              hours,
              target: orNull(draft.target),
              instructor: orNull(draft.instructor),
              material: orNull(draft.material),
              content: draft.content.split('\n'),
            },
      },
      {
        onSuccess: () => setSaved(reset ? '기본값으로 되돌렸습니다.' : '저장했습니다.'),
        onError: (cause) => setError(errorMessage(cause)),
      },
    );
  };

  const cell: CSSProperties = { ...inputStyle, padding: '4px 6px', fontSize: '13px' };
  const field = (label: string, value: string, onChange: (value: string) => void, type: 'text' | 'number' = 'text') => (
    <label className="sf-field">
      <span>{label}</span>
      <input
        className="input"
        type={type}
        min={type === 'number' ? 0 : undefined}
        step={type === 'number' ? 0.5 : undefined}
        style={cell}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );

  return (
    <Modal
      title="안전교육일지 양식"
      onClose={onClose}
      maxWidth="1380px"
      closeDisabled={write.isPending}
      beforeClose={() => !dirty || window.confirm('저장하지 않은 양식이 있습니다. 닫을까요?')}
      actions={
        <div className="flex items-center gap-2">
          <button className="btn-secondary" disabled={write.isPending} onClick={() => run(true)}>
            기본값으로
          </button>
          <button className="btn-primary" disabled={write.isPending} onClick={() => run(false)}>
            {write.isPending ? '저장 중...' : '저장'}
          </button>
        </div>
      }
    >
      <style>{FORM_DESK_STYLES}</style>
      <div className="sf-desk">
        <div className="sf-side">
          {error && <Notice tone="warning">{error}</Notice>}
          {saved && (
            <Notice tone="info" closable onClose={() => setSaved(null)}>
              {saved}
            </Notice>
          )}
          <div className="sf-panel">
            <div className="sf-field">
              <span>교육 종류</span>
              <EducationPicker compact catalog={catalog.data} kind={pick.kind} sub={pick.sub} onPick={choose} />
            </div>
          </div>
          {ready && draft && data && (
            <div className="sf-panel">
              <div className="sf-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                {field('결재 칸 1', draft.signers[0], (v) => patch({ signers: [v, draft.signers[1]] }))}
                {field('결재 칸 2', draft.signers[1], (v) => patch({ signers: [draft.signers[0], v] }))}
              </div>
              {/* 6번 칸 이름은 6번(기타) 칸에 ■ 가 서는 교육(MSDS · 성희롱예방 …)에만 종이에 나온다. */}
              <div className="sf-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                {(data.defaults.box === 6 || !!data.defaults.boxLabel) && field('6번 칸 이름', draft.boxLabel, (v) => patch({ boxLabel: v }))}
                {field('교육시간', draft.hours, (v) => patch({ hours: v }), 'number')}
              </div>
              {field('교육대상', draft.target, (v) => patch({ target: v }))}
              {field('교육 강사', draft.instructor, (v) => patch({ instructor: v }))}
              {field('교육자료 및 보조기기', draft.material, (v) => patch({ material: v }))}
              <label className="sf-field">
                <span>교육 내용</span>
                <textarea
                  className="input"
                  style={{ ...cell, minHeight: '280px', resize: 'vertical', lineHeight: 1.5 }}
                  value={draft.content}
                  onChange={(e) => patch({ content: e.target.value })}
                />
              </label>
            </div>
          )}
        </div>
        <div className="sf-paper">
          {ready && draft && data ? (
            <JournalPaper
              form={DEFAULT_FORMS.EDUCATION}
              journal={previewJournal('EDUCATION', {
                educationKind: data.kind as EducationKind,
                educationSubKind: data.sub,
                educationHours: hours,
                target: orNull(draft.target),
                instructor: orNull(draft.instructor),
                educationMaterial: orNull(draft.material),
                content: draft.content.trim() === '' ? null : draft.content,
              })}
              siteName=""
              kindLabel=""
              photos={[]}
              education={{
                sub: { ...data.current, signers: draft.signers, boxLabel: orNull(draft.boxLabel) },
                boxes: catalog.data?.boxes ?? [],
              }}
            />
          ) : (
            template.isLoading && <LoadingBlock />
          )}
        </div>
      </div>
    </Modal>
  );
}

/** 편집 칸의 값 — 교육 내용은 한 칸 글(줄바꿈)로 고치고 저장할 때 줄로 나눈다. */
type Draft = {
  signers: [string, string];
  boxLabel: string;
  hours: string;
  target: string;
  instructor: string;
  material: string;
  content: string;
};

function toDraft(sub: EducationSub): Draft {
  return {
    signers: [sub.signers?.[0] ?? '안전관리자', sub.signers?.[1] ?? '소장'],
    boxLabel: sub.boxLabel ?? '',
    hours: sub.hours === null || sub.hours === undefined ? '' : String(sub.hours),
    target: sub.target ?? '',
    instructor: sub.instructor ?? '',
    material: sub.material ?? '',
    content: sub.content.join('\n'),
  };
}
